import React, { useCallback, useMemo, useContext } from 'react'
import { Checkbox, makeStyles } from '@material-ui/core'
import OptionsMenu from '../../Common/OptionsMenu/OptionsMenu'
import SectionFormContainer from '../components/SectionFormContainer'
import DaycareWizardContext from './DaycareWizardContext'
import useDaycareVisit from '../../../hooks/useDaycareVisit'
import { FOOD_SCHEDULE_OPTIONS } from './constants'
import { FONT_WEIGHTS } from '../../../utils'

const useStyles = makeStyles(() => ({
	checkBoxContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: '.5rem',
		marginTop: '1rem',
		fontSize: '0.875rem',
		fontWeight: FONT_WEIGHTS.SemiBold,
		'& .MuiCheckbox-root': {
			padding: 0
		}
	}
}))

const FoodSchedule = (props) => {
	const classes = useStyles()
	const {
		wizardContextProvider: { wizard_state, setWizardAttributeAndValues }
	} = useContext(DaycareWizardContext)
	const {
		attr,
		_id: pet_id,
		name,
		subtext = '',
		copy_to_all_attr,
		petNames,
		first_view_of_multiple = false
	} = props
	const { loading, facility } = useDaycareVisit()

	const title = useMemo(() => `Food Schedule for ${wizard_state[copy_to_all_attr] === true ? petNames : name}`, [
		name,
		wizard_state,
		copy_to_all_attr
	])

	const showCopyToAllCheckbox = useMemo(() => {
		return (
			first_view_of_multiple && wizard_state['pets'].length > 1 && copy_to_all_attr && copy_to_all_attr > ''
		)
	}, [first_view_of_multiple, wizard_state, copy_to_all_attr])

	const visitDate = useMemo(() => {
		const { dates } = wizard_state
		const [apptDate] = dates
		return apptDate
	}, [wizard_state])

	const handleCopyToAll = useCallback(
		(e) => {
			if (e.target && e.target.checked !== undefined) {
				const newValues = { ...wizard_state }
				newValues[copy_to_all_attr] = e.target.checked
				setWizardAttributeAndValues(newValues)
			}
		},
		[wizard_state, setWizardAttributeAndValues, attr, copy_to_all_attr]
	)

	const handleSelection = useCallback(
		(addon) => {
			const newValues = { ...wizard_state }
			let newAddOnArray = []
			const { category, time } = addon
			const hasAddon = wizard_state[attr].findIndex((a) => {
				return a.category === category && a.time === time && a.pet_id === pet_id
			})

			if (hasAddon > -1) {
				newAddOnArray = newValues[attr].filter((a) => {
					if (a.pet_id !== pet_id) {
						return true
					}
					if (a.category !== category) {
						return true
					}
					return a.time !== time && a.pet_id === pet_id
				})
			} else {
				newAddOnArray = [...newValues[attr], { ...addon, pet_id, date: visitDate }]
			}

			newValues[attr] = newAddOnArray
			setWizardAttributeAndValues(newValues)
		},
		[wizard_state, setWizardAttributeAndValues, attr, pet_id, visitDate]
	)

	const options = useMemo(() => {
		const o = FOOD_SCHEDULE_OPTIONS.map(({ title, description, disabledWhen, category, time }) => {
			let disabled = loading
			if (disabledWhen) {
				let disabledAttrsFound = false
				Object.entries(disabledWhen).forEach(([other_attr, value]) => {
					if (wizard_state[other_attr] === value) {
						disabledAttrsFound = true
					}
				})
				disabled = disabledAttrsFound || loading
			}

			const selected =
				wizard_state[attr].findIndex((a) => {
					return a.category === category && a.time === time && a.pet_id === pet_id
				}) > -1

			return {
				title,
				description,
				disabled,
				selected,
				onSelected: () => handleSelection({ category, time, pet_id })
			}
		})

		return o
	}, [handleSelection, loading, wizard_state, attr, facility, pet_id])

	return (
		<SectionFormContainer title={title} subtext={subtext}>
			<OptionsMenu options={options} />
			{showCopyToAllCheckbox ? (
				<div className={classes.checkBoxContainer}>
					<Checkbox onChange={handleCopyToAll} checked={wizard_state[copy_to_all_attr] === true} />
					<div>Copy schedule for all dogs</div>
				</div>
			) : null}
		</SectionFormContainer>
	)
}

export default FoodSchedule
